import { useEffect, useState } from "react";
import { groundopsApi } from "@/api";
import { FatigueStatusBadge, ScoreDisplay } from "@/components/FatigueUtils";
import { MapPin, AlertTriangle, Users, Activity } from "lucide-react";

export default function GroundOps() {
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    groundopsApi.getStations().then(r => setStations(r.data)).finally(() => setLoading(false));
  }, []);

  const sorted = [...stations].sort((a, b) => b.risk_score - a.risk_score);

  return (
    <div className="p-6 space-y-5 fade-in-up" data-testid="ground-ops-board">
      <div>
        <div className="text-xs tracking-widest uppercase text-muted-foreground font-bold mb-1">Module 6</div>
        <h1 className="text-2xl font-black font-heading text-foreground">Ground Operations Risk</h1>
      </div>

      {/* Station Risk Overview */}
      <div className="bg-card border border-border rounded-sm p-4">
        <div className="text-xs tracking-widest uppercase text-muted-foreground font-bold mb-4">Station Risk Heat Map</div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {sorted.map(s => {
            let bg = "bg-green-500/20 border-green-500/30 text-green-400";
            if (s.risk_score >= 75) bg = "bg-red-900/40 border-red-500/50 text-red-300";
            else if (s.risk_score >= 60) bg = "bg-red-500/20 border-red-500/30 text-red-400";
            else if (s.risk_score >= 40) bg = "bg-amber-500/15 border-amber-500/30 text-amber-400";
            return (
              <div key={s.id} className={`heatmap-cell border rounded-sm p-3 text-center cursor-pointer ${bg}`} data-testid={`station-${s.station}`}>
                <div className="text-xl font-mono font-medium">{s.risk_score}</div>
                <div className="text-xs font-bold mt-1 tracking-widest">{s.station}</div>
                <div className="text-xs opacity-70 mt-0.5">{s.name}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Station Detail Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <div className="col-span-3 text-center py-8 text-muted-foreground text-sm">Loading stations...</div>
        ) : sorted.map(s => (
          <div key={s.id} className={`bg-card border rounded-sm p-4 ${s.risk_score >= 70 ? "border-red-500/20" : "border-border"}`} data-testid={`station-card-${s.station}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <MapPin size={14} className="text-muted-foreground" />
                <h3 className="font-semibold text-foreground font-heading">{s.name} <span className="font-mono text-muted-foreground text-xs">({s.station})</span></h3>
              </div>
              <FatigueStatusBadge status={s.status} />
            </div>

            <div className="flex items-end gap-2 mb-4">
              <ScoreDisplay score={s.risk_score} />
              <span className="text-xs text-muted-foreground mb-0.5">shift risk score</span>
            </div>

            <div className="grid grid-cols-2 gap-2 mb-3">
              {[
                ["Shift Intensity", `${s.shift_intensity}%`, s.shift_intensity > 80 ? "text-red-400" : "text-foreground"],
                ["Team Size", s.team_size, "text-foreground"],
                ["Staffing Gap", s.staffing_gap > 0 ? `-${s.staffing_gap}` : "Staffed", s.staffing_gap > 0 ? "text-red-400" : "text-green-400"],
                ["Overtime Hrs", `${s.overtime_hours}h`, s.overtime_hours > 15 ? "text-red-400" : s.overtime_hours > 8 ? "text-amber-400" : "text-foreground"],
              ].map(([lbl, val, col]) => (
                <div key={lbl} className="bg-background border border-border rounded-sm p-2">
                  <div className="text-xs text-muted-foreground">{lbl}</div>
                  <div className={`text-sm font-mono font-medium mt-0.5 ${col}`}>{val}</div>
                </div>
              ))}
            </div>

            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Activity size={11} />
              <span>{s.active_turnarounds} active turnarounds</span>
              {s.staffing_gap > 0 && (
                <span className="flex items-center gap-1 text-red-400 ml-auto">
                  <AlertTriangle size={11} /> Understaffed
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
